import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Loader2, Sparkles, LayoutGrid, Users, RotateCcw, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { DayGrid, StaffBoard, StaffTray } from './PlannerViews';

const snap = (a) => a ? { primary_id: a.primary_id || '', alternate_ids: a.alternate_ids || [] } : { primary_id: '', alternate_ids: [] };
const same = (a, b) => a.primary_id === b.primary_id && JSON.stringify(a.alternate_ids) === JSON.stringify(b.alternate_ids);

export const calcImpact = (map, parts, staffById) => {
  let covered = 0, famOk = 0, dayCovSum = 0, dayCovN = 0;
  const loads = {}, warnings = [];
  for (const p of parts) {
    const a = map[p.name];
    if (!a?.primary_id) continue;
    covered++;
    const st = staffById[a.primary_id];
    if (!st) continue;
    loads[a.primary_id] = (loads[a.primary_id] || 0) + 1;
    if ((p.fam || {})[a.primary_id]) famOk++;
    else if (p.total_shifts > 0) warnings.push(`${st.name} has never worked with ${p.name} — plan a training handoff`);
    if (p.attend_days.length) {
      dayCovSum += p.attend_days.filter(d => st.available_days.includes(d)).length / p.attend_days.length;
      dayCovN++;
      const miss = p.attend_days.filter(d => !st.available_days.includes(d));
      if (miss.length) warnings.push(`${st.name} isn't available ${miss.map(d => d.toUpperCase()).join('/')} but ${p.name} attends then`);
    }
  }
  Object.entries(loads).forEach(([sid, n]) => { if (n >= 6) warnings.push(`${staffById[sid]?.name} would carry ${n} caseloads — heavy`); });
  return { covered, famOk, dayCov: dayCovN ? Math.round(dayCovSum / dayCovN * 100) : 0,
           maxLoad: Math.max(0, ...Object.values(loads)), loads, warnings };
};

const Metric = ({ label, before, after, suffix = '' }) => {
  const up = after > before, down = after < before;
  return (
    <div className="px-3 py-1.5">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#8E85A0]">{label}</div>
      <div className="text-sm font-extrabold text-[#2D1B4E]">
        <span className="text-[#B9B2C9]">{before}{suffix}</span>
        <span className="mx-1 text-[#B9B2C9]">→</span>
        <span className={up ? 'text-emerald-600' : down ? 'text-red-600' : ''}>{after}{suffix}</span>
      </div>
    </div>
  );
};

export default function PlannerBoard({ lookback, onStagedChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [dropMode, setDropMode] = useState('primary');
  const [draft, setDraft] = useState({});
  const [original, setOriginal] = useState({});
  const [reasons, setReasons] = useState({});
  const [aiSummary, setAiSummary] = useState('');
  const [scenario, setScenario] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [pushDrafts, setPushDrafts] = useState(true);
  const [weeks, setWeeks] = useState(2);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/caseloads/planner', { params: { lookback_days: lookback } });
      setData(d);
      const orig = {};
      d.participants.forEach(p => { orig[p.name] = snap(p.assigned); });
      setOriginal(orig);
      setDraft(JSON.parse(JSON.stringify(orig)));
      setReasons({}); setAiSummary('');
    } catch (e) { toast.error('Failed to load planner'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lookback]);

  const staffById = useMemo(() => Object.fromEntries((data?.staff || []).map(s => [s.id, s])), [data]);
  const staged = useMemo(() => Object.keys(draft).filter(n => !same(draft[n], original[n] || snap(null))), [draft, original]);
  useEffect(() => { onStagedChange?.(staged.length); return () => onStagedChange?.(0); /* eslint-disable-next-line */ }, [staged.length]);
  const before = useMemo(() => data ? calcImpact(original, data.participants, staffById) : null, [data, original, staffById]);
  const after = useMemo(() => data ? calcImpact(draft, data.participants, staffById) : null, [data, draft, staffById]);

  const setPrimary = (pname, sid) => setDraft(d => {
    const cur = d[pname] || snap(null);
    return { ...d, [pname]: { primary_id: sid, alternate_ids: cur.alternate_ids.filter(x => x !== sid) } };
  });
  const addAlternate = (pname, sid) => setDraft(d => {
    const cur = d[pname] || snap(null);
    if (cur.primary_id === sid || cur.alternate_ids.includes(sid)) return d;
    return { ...d, [pname]: { ...cur, alternate_ids: [...cur.alternate_ids, sid].slice(0, 3) } };
  });
  const dropStaff = (pname, sid) => (dropMode === 'primary' ? setPrimary(pname, sid) : addAlternate(pname, sid));
  const clearPrimary = (pname) => setDraft(d => ({ ...d, [pname]: { ...(d[pname] || snap(null)), primary_id: '' } }));
  const removeAlt = (pname, sid) => setDraft(d => ({ ...d, [pname]: { ...d[pname], alternate_ids: d[pname].alternate_ids.filter(x => x !== sid) } }));
  const revert = (pname) => setDraft(d => ({ ...d, [pname]: JSON.parse(JSON.stringify(original[pname] || snap(null))) }));
  const discard = () => { setDraft(JSON.parse(JSON.stringify(original))); setReasons({}); setAiSummary(''); };

  const stageScenario = async () => {
    if (!scenario.trim()) return toast.error('Type a scenario first');
    setAiBusy(true);
    try {
      const { data: r } = await api.post('/caseloads/planner/ai-scenario', { scenario, lookback_days: lookback });
      if (!r.moves.length) { toast.error('AI returned no usable moves — try rephrasing'); setAiBusy(false); return; }
      setDraft(d => {
        const next = { ...d };
        r.moves.forEach(m => { next[m.participant] = { primary_id: m.primary_id, alternate_ids: m.alternate_ids }; });
        return next;
      });
      setReasons(prev => ({ ...prev, ...Object.fromEntries(r.moves.map(m => [m.participant, m.reason])) }));
      setAiSummary(r.summary);
      toast.success(`${r.moves.length} moves staged on the board${r.skipped.length ? ` · ${r.skipped.length} skipped` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI scenario failed'); }
    setAiBusy(false);
  };

  const apply = async () => {
    setApplyBusy(true);
    try {
      const assignments = staged.map(n => ({ participant: n, ...draft[n] }));
      const { data: r } = await api.post('/caseloads/planner/apply', { assignments, push_draft_shifts: pushDrafts, weeks, lookback_days: lookback });
      toast.success(`${r.saved} caseloads saved${r.drafts_created ? ` · ${r.drafts_created} draft shifts created` : ''}`);
      if (r.skipped?.length) toast.info(`${r.skipped.length} drafts skipped (conflicts/PTO/no pattern)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    setApplyBusy(false);
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;
  if (!data) return null;
  const parts = data.participants.filter(p => p.active || p.total_shifts > 0);

  return (
    <div className="space-y-3" data-testid="caseload-planner">
      <div className="rounded-xl border border-[#DED8EB] bg-white p-3 space-y-2" data-testid="planner-ai-box">
        <label className="text-xs font-bold uppercase tracking-wider text-[#6B3FA0] flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> Describe a scenario — AI stages it visually on the board
        </label>
        <div className="flex gap-2">
          <input value={scenario} onChange={e => setScenario(e.target.value)}
                 placeholder={'e.g. "Move everyone off Tim and spread his caseload" · "Give every participant a primary who matches their days"'}
                 className="flex-1 rounded-lg border border-[#DED8EB] px-3 py-2 text-sm text-[#2D1B4E] placeholder:text-[#B9B2C9] focus:outline-none focus:border-[#9B7ED8]"
                 data-testid="planner-scenario-input" />
          <Button onClick={stageScenario} disabled={aiBusy} className="bg-[#2D1B4E] hover:bg-[#3D2A63] text-white gap-1.5 shrink-0" data-testid="planner-ai-stage-btn">
            {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {aiBusy ? 'Modeling…' : 'Stage scenario'}
          </Button>
        </div>
        {aiSummary && <p className="text-xs text-[#5A4D6E] bg-[#FDFBF4] border border-[#EADFC2] rounded-lg px-2.5 py-1.5" data-testid="planner-ai-summary">{aiSummary}</p>}
      </div>

      <div className="rounded-xl border border-[#DED8EB] bg-white flex items-stretch divide-x divide-[#EEE9F6] flex-wrap" data-testid="planner-impact">
        <Metric label="Primaries set" before={before.covered} after={after.covered} />
        <Metric label="Day coverage" before={before.dayCov} after={after.dayCov} suffix="%" />
        <Metric label="Familiar primaries" before={before.famOk} after={after.famOk} />
        <Metric label="Max staff load" before={before.maxLoad} after={after.maxLoad} />
        <div className="px-3 py-1.5 flex-1 min-w-[200px]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8E85A0]">Warnings in this model</div>
          {after.warnings.length === 0
            ? <div className="text-sm font-bold text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> none</div>
            : <div className="text-[11px] text-amber-700 leading-tight max-h-12 overflow-y-auto" data-testid="planner-warnings">
                {after.warnings.slice(0, 6).map((w, i) => <div key={i} className="flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{w}</div>)}
              </div>}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-full border border-[#DED8EB] overflow-hidden">
          {[['grid', 'Day grid', LayoutGrid], ['staff', 'Staff board', Users]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setView(k)}
                    className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${view === k ? 'bg-[#6B3FA0] text-white' : 'bg-white text-[#5A4D6E] hover:bg-[#F0EDF6]'}`}
                    data-testid={`planner-view-${k}`}><Icon className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
        {view === 'grid' && (
          <div className="flex rounded-full border border-[#DED8EB] overflow-hidden" data-testid="planner-dropmode">
            {[['primary', 'Drop sets: Primary ★'], ['alternate', 'Drop sets: Alternate ✓']].map(([k, l]) => (
              <button key={k} onClick={() => setDropMode(k)}
                      className={`px-3 py-1.5 text-xs font-bold ${dropMode === k ? 'bg-[#2D1B4E] text-white' : 'bg-white text-[#5A4D6E] hover:bg-[#F0EDF6]'}`}
                      data-testid={`dropmode-${k}`}>{l}</button>
            ))}
          </div>
        )}
        <span className="text-[11px] text-[#8E85A0]">Drag {view === 'grid' ? 'a staff chip onto any participant' : 'participant chips between staff cards'} — nothing is real until you Apply.</span>
      </div>

      {view === 'grid' ? (
        <>
          <StaffTray staff={data.staff} loads={after.loads} />
          <DayGrid parts={parts} draft={draft} staffById={staffById} staged={staged} onDropStaff={dropStaff}
                   onClearPrimary={clearPrimary} onRemoveAlt={removeAlt} lineLabels={data.line_labels} />
        </>
      ) : (
        <StaffBoard staff={data.staff} parts={parts} draft={draft} staged={staged} loads={after.loads}
                    onMove={setPrimary} onClearPrimary={clearPrimary} lineLabels={data.line_labels} />
      )}

      {staged.length > 0 && (
        <div className="sticky bottom-2 rounded-xl border-2 border-[#6B3FA0] bg-white shadow-lg p-3 space-y-2" data-testid="planner-apply-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-extrabold text-[#2D1B4E]">{staged.length} staged change{staged.length > 1 ? 's' : ''}</span>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-[#5A4D6E] ml-2">
              <input type="checkbox" checked={pushDrafts} onChange={e => setPushDrafts(e.target.checked)} data-testid="planner-push-drafts" />
              Also create draft shifts for the next
            </label>
            <select value={weeks} onChange={e => setWeeks(+e.target.value)} disabled={!pushDrafts}
                    className="rounded border border-[#DED8EB] text-xs font-bold px-1.5 py-1" data-testid="planner-weeks">
              {[1, 2, 3, 4].map(w => <option key={w} value={w}>{w} week{w > 1 ? 's' : ''}</option>)}
            </select>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={discard} className="gap-1.5 border-[#DED8EB]" data-testid="planner-discard-btn"><RotateCcw className="h-4 w-4" /> Discard</Button>
              <Button onClick={apply} disabled={applyBusy} className="bg-[#6B3FA0] hover:bg-[#5A3390] text-white gap-1.5" data-testid="planner-apply-btn">
                {applyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Apply model
              </Button>
            </div>
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1" data-testid="planner-diff">
            {staged.map(n => {
              const o = original[n] || snap(null), d = draft[n];
              return (
                <div key={n} className="flex items-center gap-2 text-xs rounded-lg bg-[#F7F4FB] px-2.5 py-1" data-testid={`diff-${n}`}>
                  <b className="text-[#2D1B4E]">{n}</b>
                  <span className="text-[#B9B2C9]">{staffById[o.primary_id]?.name || '—'}</span>
                  <span className="text-[#8E85A0]">→</span>
                  <span className="font-bold text-[#6B3FA0]">{staffById[d.primary_id]?.name || '— (cleared)'}</span>
                  {d.alternate_ids.length > 0 && <span className="text-[#8E85A0]">alts: {d.alternate_ids.map(x => staffById[x]?.name).join(', ')}</span>}
                  {reasons[n] && <span className="text-[10px] italic text-[#8A6D1F] truncate max-w-[280px]" title={reasons[n]}>{reasons[n]}</span>}
                  <button onClick={() => revert(n)} className="ml-auto text-[#8E85A0] hover:text-red-500" title="Revert" data-testid={`revert-${n}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
